import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChildren,
  ViewEncapsulation,
} from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { TimesPipe } from '@core';
import { type ChildDevice, type DeviceChart, type DeviceData } from '@devices';
import { DeviceService } from '@devices';
import { TippyDirective } from '@ngneat/helipopper';
import { AvsDateRangeIds, DateRangeSelect$, getAvsDateRange } from '@ui';
import { addHours, endOfHour, startOfHour } from 'date-fns';
import { forkJoin } from 'rxjs';

export type BarState = 'connected' | 'disconnected' | 'no-data' | 'paused';

const BAR_COLOR = {
  connected: 'var(--green-100)',
  disconnected: '#E0111C',
  'no-data': 'rgba(112, 113, 117, 0.10)',
  'paused': 'rgba(112, 113, 117, 0.10)',
};

const STATE_LABEL = {
  connected: 'Online',
  disconnected: 'Offline',
  'no-data': 'No data',
  'paused': 'Paused',
};

const STATE_COLOR = {
  connected: 'var(--green-100)',
  disconnected: '#E0111C',
  'no-data': 'var(--gray-100)',
  'paused': 'var(--gray-100)',
};

interface HourBar {
  backgroundColor: string;
  dateLabel: string;
  endTime: Date;
  hour: number;
  startTime: Date;
  state: BarState;
  stateColor: string;
  stateLabel: string;
  timeLabel: string;
}

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, TippyDirective, TimesPipe],
  selector: 'avs-device-connection-status-widget',
  styleUrl: './device-connection-status-widget.component.scss',
  templateUrl: './device-connection-status-widget.component.html',
})
export class DeviceConnectionStatusWidgetComponent {
  device = input.required<DeviceData | ChildDevice>();

  protected hourBars = signal<HourBar[]>([]);
  protected isLoading = signal(false);

  private destroyRef = inject(DestroyRef);
  private deviceService = inject(DeviceService);
  private loadedDeviceId: string | null = null;
  private tooltips = viewChildren(TippyDirective);

  constructor() {
    effect(() => {
      const device = this.device();

      if (device?.id) {
        const showLoader = device.id !== this.loadedDeviceId;

        untracked(() => this.loadData(showLoader));
      }
    });

    this.scheduleHourlyRefresh();
  }

  hideTooltips() {
    this.tooltips().forEach(tooltip => tooltip.hide());
  }

  protected onBarClick(bar: HourBar) {
    DateRangeSelect$.next(new DateRange(bar.startTime, bar.endTime));
  }

  private scheduleHourlyRefresh() {
    const now = new Date();
    const msUntilNextHour =
      (60 - now.getMinutes()) * 60 * 1000 -
      now.getSeconds() * 1000 -
      now.getMilliseconds();

    const timeoutId = setTimeout(() => {
      this.loadData(false);

      const intervalId = setInterval(() => this.loadData(false), 60 * 60 * 1000);

      this.destroyRef.onDestroy(() => clearInterval(intervalId));
    }, msUntilNextHour);

    this.destroyRef.onDestroy(() => clearTimeout(timeoutId));
  }

  private buildHourBars(connected: DeviceChart | null, paused: DeviceChart | null) {
    const currentHourStart = startOfHour(new Date());
    const bars: HourBar[] = [];

    for (let i = 0; i < 24; i++) {
      const startTime = addHours(currentHourStart, i - 23);
      const endTime = endOfHour(startTime);

      const connectedPoints = this.getPointsInRange(connected?.timeseries ?? [], startTime, endTime);
      const pausedPoints = this.getPointsInRange(paused?.timeseries ?? [], startTime, endTime);

      const wasOffline = connectedPoints.some(p => p.value === 0);
      const wasOnline = connectedPoints.some(p => p.value === 1);
      const wasPaused = pausedPoints.some(p => p.value === 1);

      // Determine state with priority: offline > paused > online > no-data
      let state: BarState;

      if (wasOffline) {
        state = 'disconnected';
      } else if (wasPaused) {
        state = 'paused';
      } else if (wasOnline) {
        state = 'connected';
      } else {
        state = 'no-data';
      }

      const stateLabel = STATE_LABEL[state];

      bars.push({
        backgroundColor: BAR_COLOR[state],
        dateLabel: startTime.toLocaleDateString('en-US', { day: 'numeric', month: 'short' }),
        endTime,
        hour: i,
        startTime,
        state,
        stateColor: STATE_COLOR[state],
        stateLabel,
        timeLabel: startTime.toLocaleTimeString('en-US', { hour: 'numeric', hour12: true }),
      });
    }

    return bars;
  }

  private getPointsInRange(timeseries: { timeStamp: string; value: number }[], start: Date, end: Date) {
    return timeseries.filter(p => {
      const t = new Date(p.timeStamp).getTime();

      return t >= start.getTime() && t <= end.getTime();
    });
  }

  private loadData(showLoader = true) {
    const device = this.device();
    const range = getAvsDateRange(AvsDateRangeIds.LAST_24_HOURS);
    const start = range.start!.toISOString();
    const end = range.end!.toISOString();

    this.isLoading.set(showLoader);

    forkJoin({
      connected: this.deviceService.getChartsData({
        data: { fromDateTimeUtc: start, point: 'connected', toDateTimeUtc: end },
        deviceId: device.id,
      }),
      paused: this.deviceService.getChartsData({
        data: { fromDateTimeUtc: start, point: 'paused', toDateTimeUtc: end },
        deviceId: device.id,
      }),
    }).subscribe({
      complete: () => this.isLoading.set(false),
      next: ({ connected, paused }) => {
        this.isLoading.set(false);
        this.loadedDeviceId = device.id;
        this.hourBars.set(this.buildHourBars(connected, paused));
      },
    });
  }
}
